import React from 'react';
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  Box,
  Paper,
  Popper,
  MenuItem,
  Typography,
  Divider,
  MenuList,
  Tooltip,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import DynamicFormRoundedIcon from '@mui/icons-material/DynamicFormRounded';
import ModelTrainingRoundedIcon from '@mui/icons-material/ModelTrainingRounded';

export default function SubscriptionActionsCell({
  row,
  onView,
  onEdit,
  onDelete,
  onAlgorithmInputOpen,
  onAlgorithmInputFormOpen,
  onOccupancyPredictionAnalysisOpen,
}) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMenuItemClick = (option) => {
    setOpen(false);
    if (option === 'view') {
      onView(row);
    }
    if (option === 'edit') {
      onEdit(row);
    }
    if (option === 'delete') {
      onDelete(row);
    }
    if (option === 'algorithmInput') {
      onAlgorithmInputOpen(row);
    }
    if (option === 'algorithmInputForm') {
      onAlgorithmInputFormOpen(row);
    }
    if (option === 'occupancyModel') {
      onOccupancyPredictionAnalysisOpen(row);
    }
  };

  return (
    <>
      <ButtonGroup
        variant="outlined"
        size="small"
        ref={anchorRef}
        aria-label="split button"
        sx={{
          '& .MuiButtonGroup-grouped:not(:last-of-type)': {
            borderRight: 'none',
          },
        }}
      >
        <Button
          size="small"
          onClick={() => handleMenuItemClick('view')}
          sx={{
            width: 75,
            borderColor: 'divider',
            ':hover': { borderColor: 'divider' },
          }}
          //   startIcon={<EditRoundedIcon />}
        >
          View
        </Button>
        <Button
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          variant="outlined"
          onClick={handleToggle}
          sx={{
            borderColor: 'divider',
            ':hover': { borderColor: 'divider', borderRightColor: 'divider' },
          }}
        >
          <ArrowDropDownIcon fontSize="small" />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        style={{ zIndex: 1301 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  id="split-button-menu"
                  autoFocusItem
                  dense
                  sx={{ p: 1 }}
                >
                  <Tooltip
                    title="Use this option to input algorithm parameters via a JSON editor."
                    placement="left"
                    arrow
                    enterDelay={500}
                  >
                    <MenuItem
                      onClick={() => handleMenuItemClick('algorithmInput')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <DataObjectRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          Algorithm Input
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Tooltip>

                  <Tooltip
                    title="Use this option to input algorithm parameters manually via a form."
                    placement="left"
                    arrow
                    enterDelay={500}
                  >
                    <MenuItem
                      onClick={() => handleMenuItemClick('algorithmInputForm')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <DynamicFormRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          Algorithm Input
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Tooltip>

                  <Tooltip
                    title="Use this option to view the occupancy model and generate a new occupancy analysis."
                    placement="left"
                    arrow
                    enterDelay={500}
                  >
                    <MenuItem
                      onClick={() => handleMenuItemClick('occupancyModel')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <ModelTrainingRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          Occupancy Model
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Tooltip>

                  <Divider></Divider>
                  <Tooltip
                    title="Edit subscription details such as name, data and user input."
                    placement="left"
                    arrow
                    enterDelay={500}
                  >
                    <MenuItem
                      onClick={() => handleMenuItemClick('edit')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <EditRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          Edit
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Tooltip>

                  <Tooltip
                    title="Delete this subscription."
                    placement="left"
                    arrow
                    enterDelay={500}
                  >
                    <MenuItem
                      onClick={() => handleMenuItemClick('delete')}
                      sx={{
                        py: '6px',
                        px: '12px',
                        borderRadius: '5px',
                        ':hover': {
                          color: 'error.main',
                        },
                        transition: 'color 0.3s ease',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <DeleteRoundedIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" fontWeight="medium">
                          Delete
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Tooltip>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
